import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Clock, Users, Settings, Tablet, CheckCircle, ArrowRight } from 'lucide-react';
import BrandLogo, { BrandMark } from '@/components/BrandLogo';
import SiteLegalLinks from '@/components/SiteLegalLinks';
import {
  OPERATOR_LEGAL_NAME,
  OPERATOR_COMPANY_NUMBER,
  PRODUCT_NAME,
} from '@/legal/companyInfo';
import restaurantBackground from '@/assets/admin-restaurant-background.png';

export default function LandingPage() {
  const [demoId] = useState('demo-restaurant');

  const features = [
    {
      icon: Calendar,
      title: 'Smart Calendar',
      description: 'Visual calendar with real-time availability. Customers see open slots instantly.'
    },
    {
      icon: Clock,
      title: 'Time Slot Management',
      description: 'Configure booking intervals, tables per slot, and operating hours with ease.'
    },
    {
      icon: Users,
      title: 'Customer Management',
      description: 'Track customer history, preferences, and build lasting relationships.'
    },
    {
      icon: Settings,
      title: 'White-Label Ready',
      description: 'Customize colors, logo, and branding to match your restaurant identity.'
    },
    {
      icon: Tablet,
      title: 'Tablet Operations',
      description: 'Dedicated view for host stand with hourly timeline and quick status updates.'
    },
    {
      icon: CheckCircle,
      title: 'Instant Confirmations',
      description: 'Automated email confirmations and reminders keep customers informed.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandLogo size="sm" textClassName="text-white" />
          <div className="flex items-center gap-4">
            <Link to="/admin/login">
              <Button variant="ghost" className="text-white/75 hover:text-white hover:bg-white/10" data-testid="login-link">Sign In</Button>
            </Link>
            <Link to="/admin/register">
              <Button className="bg-white text-slate-950 hover:bg-amber-50" data-testid="get-started-btn">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden px-6 py-20 lg:py-28">
        <img
          src={restaurantBackground}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/88 to-slate-950/45" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_34%_32%,rgba(245,158,11,0.20),transparent_36%)]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
            <div className="space-y-8 animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-100/20 bg-white/10 text-amber-100 text-sm font-medium backdrop-blur-md">
                <CheckCircle className="h-4 w-4 text-amber-200" />
                Premium restaurant booking software
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white">
                Reservations that feel as polished as your restaurant.
              </h1>
              <p className="text-lg sm:text-xl text-white/72 leading-relaxed max-w-2xl">
                DineSpot gives restaurants a beautiful booking widget, a calm admin console, deposit handling, reminders, and calendar sync, without making staff fight clunky software during service.
              </p>
              <div className="inline-flex items-center gap-2 rounded-lg border border-emerald-200/30 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-100">
                <span>14-day free trial</span>
                <span className="text-emerald-200/70">•</span>
                <span className="font-semibold">£39/month per restaurant</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/admin/register">
                  <Button size="lg" className="w-full sm:w-auto gap-2 bg-white text-slate-950 hover:bg-amber-50" data-testid="hero-cta">
                    Start Your Restaurant Setup
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to={`/book/${demoId}`}>
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/20 bg-white/10 text-white hover:bg-white/15 hover:text-white" data-testid="demo-widget-btn">
                    View Demo Widget
                  </Button>
                </Link>
              </div>
              <div className="grid max-w-2xl gap-4 pt-4 sm:grid-cols-3">
                {['Take deposits', 'Reduce no-shows', 'Sync calendars'].map((item) => (
                  <div key={item} className="rounded-2xl border border-white/15 bg-white/10 p-4 text-sm text-white/80 backdrop-blur-md">
                    <CheckCircle className="mb-3 h-4 w-4 text-amber-200" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative animate-fade-in" style={{ animationDelay: '200ms' }}>
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-amber-200/25 via-white/10 to-slate-700/20 blur-2xl" />
              <Card className="relative overflow-hidden border-white/15 bg-white/95 text-slate-950 shadow-2xl shadow-black/40 backdrop-blur-xl">
                <div 
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url(${restaurantBackground})` }}
                />
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading font-semibold text-lg">The Golden Fork</h3>
                      <p className="text-sm text-muted-foreground">Tonight's availability</p>
                    </div>
                    <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-amber-700" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {['6:00 PM', '7:30 PM', '8:00 PM'].map((time) => (
                      <div key={time} className="text-center p-2 rounded-lg bg-slate-100 text-sm font-medium">
                        {time}
                      </div>
                    ))}
                  </div>
                  <Button className="w-full bg-slate-950 text-white hover:bg-slate-900" disabled>Book Now</Button>
                  <div className="grid grid-cols-3 gap-3 border-t border-slate-200 pt-4 text-center">
                    <div>
                      <p className="font-semibold">42</p>
                      <p className="text-xs text-muted-foreground">covers</p>
                    </div>
                    <div>
                      <p className="font-semibold">£180</p>
                      <p className="text-xs text-muted-foreground">deposits</p>
                    </div>
                    <div>
                      <p className="font-semibold">Live</p>
                      <p className="text-xs text-muted-foreground">calendar</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-slate-50 text-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-amber-700">Built for service</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-slate-950">
              Everything a restaurant needs to stay in control
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A customer-facing booking experience that looks premium, with practical tools for staff behind the scenes.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            {features.map((feature, index) => (
              <Card key={index} className="border-slate-200 bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-12 w-12 rounded-xl bg-slate-950 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-amber-100" />
                  </div>
                  <CardTitle className="font-heading text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(245,158,11,0.20),transparent_32%)]" />
        <div className="max-w-4xl mx-auto text-center">
          <Card className="relative border-white/15 bg-white/10 p-12 text-white shadow-2xl shadow-black/30 backdrop-blur-xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Make your booking experience match your hospitality.
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              Give guests a smooth way to book, and give your team the calm tools they need to run service.
              Start with a <strong>14-day free trial</strong>, then one clear plan at <strong>£39/month per restaurant</strong>.
            </p>
            <Link to="/admin/register">
              <Button size="lg" className="gap-2 bg-white text-slate-950 hover:bg-amber-50" data-testid="cta-get-started">
                Create Your Restaurant Account
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-slate-950 py-10 px-6 text-white">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between sm:gap-8 w-full">
            <div className="flex items-center gap-2">
              <BrandMark size="sm" className="rounded-lg" />
              <span className="font-heading font-semibold">{PRODUCT_NAME}</span>
            </div>
            <SiteLegalLinks variant="dark" />
          </div>
          <p className="text-center text-xs text-white/50 max-w-2xl leading-relaxed">
            © {new Date().getFullYear()} {PRODUCT_NAME}. Operated by {OPERATOR_LEGAL_NAME} (company no.{' '}
            {OPERATOR_COMPANY_NUMBER}).
          </p>
        </div>
      </footer>
    </div>
  );
}
